import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import {
  ContactDTO,
  ContactReferenceDTO,
  AddressDTO
} from "@/model/data.model";
import { GetContactResults, SearchParams } from "@/model/component.model";
import store from "../vuex/store";

/// Facade to API Contacts service
/// Implements a cache to allow local searching
export class ContactService {
  customerMbeId: string;

  constructor(customerMbeId: string) {
    this.customerMbeId = customerMbeId;
  }

  // static deletePickups(
  //   storeId: string,
  //   pickups: PickupsDTO,
  //   username: string,
  //   language: string
  // ): Promise<any> {
  //   if (pickups.content.length < 1) {
  //     throw "Pickup list should have at least one element";
  //   } else {
  //     const config: AxiosRequestConfig = {
  //       method: "DELETE",
  //       url: store.getters.BASE_API_URL + `/shipments/pickup/onlineMBE`,
  //       params: {
  //         pickupId: pickups.content[0].pickupId,
  //         storeId: storeId,
  //         username: username,
  //         language: language
  //       }
  //     };
  //     return axios.request(config).then((res: any) => {
  //       if (pickups.content.length > 1) {
  //         pickups.content.shift();
  //         return this.deletePickups(storeId, pickups, username, language);
  //       } else {
  //         return res;
  //       }
  //     });
  //   }
  // }

  getContacts(
    search: SearchParams = {
      search: "",
      page: 0,
      pageSize: 20
    } as SearchParams
  ): Promise<GetContactResults> {
    const contactIds: any[] = [];
    const config: AxiosRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/customers/search",
      params: {
        customerId: this.customerMbeId,
        sort: search.orderField + "," + search.orderDirection,
        page: search.page,
        size: search.pageSize
      },
      data: {
        contactIds: contactIds,
        businessName: search.tradename !== "" ? search.tradename : null,
        zipCode: search.zipCode !== "" ? search.zipCode : null,
        province: search.province !== "" ? search.province : null,
        city: search.city !== "" ? search.city : null,
        search: search.search !== "" ? search.search : null,
        address: search.address !== "" ? search.address : null,
        countryId: search.countryId !== "" ? search.countryId : null,
        molUsername: search.molUsername !== "" ? search.countryId : null,
        enriched: true,
        enrichedAddress: true,
        enrichedContacts: true,
        addressType: search.addressType !== "" ? search.addressType : null,
        addressNickname:
          search.addressNickname !== "" ? search.addressNickname : null,
        fromNewShipment: search.fromNewShipment
      }
    };

    return axios.request(config).then((res: AxiosResponse) => {
      const contacts = res.data.content as ContactDTO[];
      const searchParams = {
        search: search.search,
        orderField: search.orderField,
        orderDirection: search.orderDirection,
        totalElements: res.data.totalElements,
        totalPages: res.data.totalPages,
        numberOfElements: res.data.numberOfElements,
        page: res.data.pageable.pageNumber,
        pageSize: res.data.pageable.pageSize
      } as SearchParams;

      return { contacts, searchParams } as GetContactResults;
    });
  }

  getAddresses(search: SearchParams) {
    return this.getContacts(search).then((res: GetContactResults) => {
      const contacts = res.contacts;
      const searchParams = res.searchParams;
      const addresses: AddressDTO[] = [];
      contacts.map((item: ContactDTO) => {
        item.addresses.map((address: AddressDTO) => {
          addresses.push(address);
        });
      });

      return { addresses, searchParams };
    });
  }

  defaultPickup(pickupContactId: number, customerId: any) {
    const config: AxiosRequestConfig = {
      method: "PUT",
      params: {
        pickupContactId: pickupContactId
      },
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe" +
        "/pickup"
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  deleteDefaultPickup(pickupContactId: number, customerId: any) {
    const config: AxiosRequestConfig = {
      method: "DELETE",
      params: {
        pickupContactId: pickupContactId
      },
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        customerId +
        "/onlinembe" +
        "/pickup"
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  getContact(id: number, typeAddress: number): Promise<ContactDTO> {
    const contact: ContactDTO = {
      firstname: "",
      lastname: "",
      gender: "",
      contactType: "CONTACT",
      birthDate: "1980-01-01"
    } as ContactDTO;
    contact.contactsReference = [
      { type: "EMAIL1", value: "" } as ContactReferenceDTO
    ];

    contact.addresses = [
      {
        type: typeAddress == 1 ? "SHIPMENT" : "OPERATION",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        countryName: "",
        countryId: "",
        province: ""
      } as AddressDTO
    ];
    if (id === 0) {
      return Promise.resolve(contact);
    } else {
      return axios
        .get(store.getters.BASE_API_URL + "/customers/onlinembe/contact/" + id)
        .then(function(res) {
          return res.data;
        });
    }
  }

  saveContact(contact: ContactDTO): Promise<ContactDTO> {
    if (contact.id === undefined || contact.id === 0) {
      // Just create new contact
      return this.postContact(contact);
    } else {
      const new_addresses: AddressDTO[] = [];
      contact.addresses.map(item => {
        if (item.id === undefined) {
          new_addresses.push(item);
        }
      });

      contact.addresses = contact.addresses.filter(item => {
        return item.id !== undefined;
      });

      return this.updateContact(contact, new_addresses);
    }
  }

  updateContact(
    contact: ContactDTO,
    new_addresses: AddressDTO[]
  ): Promise<ContactDTO> {
    if (new_addresses.length > 0) {
      const new_address_contact: ContactDTO = {
        id: contact.id,
        firstname: contact.firstname,
        lastname: contact.lastname,
        addresses: [new_addresses.shift() as AddressDTO]
      } as ContactDTO;
      return this.postContact(new_address_contact).then(
        (updated_contact: ContactDTO) => {
          return this.updateContact(contact, new_addresses);
        }
      );
    } else {
      return this.putContact(contact);
    }
  }

  postContact(contact: ContactDTO): Promise<ContactDTO> {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: contact,
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        this.customerMbeId +
        "/contacts"
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  putContact(contact: ContactDTO): Promise<ContactDTO> {
    const config: AxiosRequestConfig = {
      method: "PUT",
      data: contact,
      url:
        store.getters.BASE_API_URL +
        "/customers/" +
        this.customerMbeId +
        "/contacts/" +
        contact.id
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  deleteContacts(contacts: ContactDTO[]): Promise<any> {
    if (contacts.length < 1) {
      throw "Contact list should have at least one element";
    } else {
      const config: AxiosRequestConfig = {
        method: "DELETE",
        url:
          store.getters.BASE_API_URL +
          "/customers/" +
          this.customerMbeId +
          "/contacts",
        data: contacts
      };
      return axios
        .request(config)
        .then((res: any) => {
          return res;
        })
        .catch(err => {
          console.error("Response failed with message: " + err.message);
          return Promise.reject(err);
        });
    }
  }

  deleteContact(id: string) {
    return axios
      .delete(
        store.getters.BASE_API_URL +
          "/customers/" +
          this.customerMbeId +
          "/contacts/" +
          id
      )
      .then(res => {
        return res.status;
      });
  }

  deleteAddress(id: number) {
    return axios
      .delete(
        store.getters.BASE_API_URL +
          "/customers/" +
          this.customerMbeId +
          "/contacts/addresses/" +
          id.toString()
      )
      .then(res => {
        return res.data;
      });
  }
}
